import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { TextArea } from 'src/components/widgets/textArea';
import { T } from 'src/shared/helpers';
import PlayArrowIcon from 'src/shared/material_icons/av/play_arrow.svg';
import styled, { keyframes } from 'styled-components';
import { PrimaryButton } from '../../widgets/buttons';
import { Icon } from '../../widgets/icon';
import { ONBOARDING_TOOLS, OnboardingTool, PROMPT_PLACEHOLDER } from './toolSuggestion';

// Every solution is a card the seller picks by what it achieves: the goal is the headline, the product
// name sits above it as a label. The whole card is the link into the tool, and its clip plays only while
// the card is hovered or focused, so the videos never all run at once.

// The flagship solution carries a badge across the top of its card.
const MOST_POPULAR_ID: OnboardingTool['id'] = 'VC';

// Virtual Try-on stays in the catalogue, so a description can still be routed to it, but it is not one
// of the cards offered here.
const LISTED_TOOLS = ONBOARDING_TOOLS.filter((tool) => tool.id !== 'VTO');

const ACCENT = '#f08035';
const INK = '#263238';
const MUTE = '#607d8b';
const LINE = '#e3e7ec';
const FOCUS = '#0054c4';

// How long each half of the swap between the cards and the description box takes.
const VIEW_SWAP_MS = 220;

const Wrapper = styled.div`
    width: 100%;
    max-width: 1260px;
`;

const Intro = styled.div`
    max-width: 1080px;
    margin: 0 auto;
    text-align: center;
`;

const TextLink = styled.button`
    padding: 0;
    border: none;
    background: none;
    font-family: ${(props) => props.theme.app.font};
    font-size: inherit;
    color: ${MUTE};
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;

    &:hover {
        color: ${INK};
    }

    &:focus-visible {
        outline: 3px solid ${FOCUS};
        outline-offset: 2px;
        border-radius: 4px;
    }
`;

const Title = styled.h1`
    margin: 0;
    font-size: 38px;
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -0.015em;
    color: ${INK};

    @media (max-width: 860px) {
        font-size: 29px;
    }
`;

const Subtitle = styled.p`
    margin: 12px 0 0;
    font-size: 16.5px;
    color: #555;

    @media (max-width: 860px) {
        font-size: 15px;
    }
`;

// Four across on a wide screen, then two by two and one as the room runs out. Each card takes a band of
// CARD_ROWS rows and lays its parts on them (see Card), so the cards of one line share their rows: the
// band is spaced from the next by the card's own margin rather than by a row gap, which would also fall
// between the parts of a card.
const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    column-gap: 18px;
    margin-top: 40px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 860px) {
        column-gap: 14px;
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

// Tells the seller the still frame is a clip; it fades out once the clip starts playing on hover.
const Preview = styled.span`
    position: absolute;
    right: 10px;
    bottom: 10px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 10px 3px 6px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 1px 4px rgba(38, 50, 56, 0.18);
    font-size: 11px;
    font-weight: 600;
    color: #555;
    transition: opacity 0.15s ease;
`;

const GoArrow = styled.i`
    font-style: normal;
    transition: transform 0.15s ease;
`;

const Go = styled.span`
    display: flex;
    align-items: center;
    gap: 7px;
    padding-top: 34px;
    font-size: 13.5px;
    font-weight: 600;
    color: ${ACCENT};
`;

// The preview, the name, the goal, the summary, the Built for line and the call to action.
const CARD_ROWS = 6;

// Its parts sit on rows shared with the other cards of the line (a subgrid), so each one starts at the
// same height on every card however long the texts above it run. Not clipped, so the Most popular badge
// can sit across the top edge; the preview rounds its own corners.
const Card = styled.a`
    position: relative;
    display: grid;
    grid-row: span ${CARD_ROWS};
    grid-template-rows: subgrid;
    margin-bottom: 18px;
    padding-bottom: 18px;
    background: #fff;
    border: 1px solid ${LINE};
    border-radius: 14px;
    color: inherit;
    text-decoration: none;
    transition:
        border-color 0.15s ease,
        transform 0.15s ease,
        box-shadow 0.15s ease;

    &:hover,
    &:focus-visible {
        border-color: #cbd3da;
        transform: translateY(-3px);
        box-shadow: 0 12px 28px -18px rgba(38, 50, 56, 0.45);

        ${Preview} {
            opacity: 0;
        }

        ${Go} {
            color: #cc540f;
        }

        ${GoArrow} {
            transform: translateX(4px);
        }
    }

    &:focus-visible {
        outline: 3px solid ${FOCUS};
        outline-offset: 3px;
    }

    @media (max-width: 860px) {
        margin-bottom: 14px;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        ${GoArrow} {
            transition: none;
        }
    }
`;

// Tinted with the solution's own colour, which shows until the clip has loaded its first frame.
const Media = styled.div<{ $background: string }>`
    position: relative;
    aspect-ratio: 16 / 10;
    overflow: hidden;
    border-radius: 13px 13px 0 0;
    background: ${(props) => props.$background};
`;

const Video = styled.video`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

// A solid pill floated across the card's top edge, near its left corner.
const PopularBadge = styled.span`
    position: absolute;
    top: 0;
    left: 16px;
    z-index: 1;
    transform: translateY(-50%);
    padding: 5px 12px;
    border-radius: 100px;
    background: ${ACCENT};
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.3;
    white-space: nowrap;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
`;

// The text parts keep the card's side padding; the preview runs edge to edge.
const CardPart = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 18px;
`;

// The product name is a label above the goal, which is what the seller reads first.
const ToolName = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 16px 18px 0;
    font-size: 13px;
    font-weight: 600;
    color: ${MUTE};
    white-space: nowrap;

    svg {
        flex-shrink: 0;
        width: 22px;
        height: 22px;
    }
`;

const ToolNameText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Goal = styled.span`
    margin-top: 10px;
    font-size: 16.5px;
    font-weight: 600;
    line-height: 1.32;
    color: ${INK};
`;

const Summary = styled.p`
    margin: 8px 0 0;
    font-size: 13.5px;
    line-height: 1.5;
    color: #555;
`;

// Set apart by a hairline: who the solution is for, under a small uppercase label.
const BuiltFor = styled.p`
    margin: 12px 0 0;
    padding-top: 12px;
    border-top: 1px solid ${LINE};
    font-size: 13px;
    line-height: 1.45;
    color: #555;
`;

const BuiltForLabel = styled.span`
    display: block;
    margin-bottom: 3px;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${MUTE};
`;

// The cards' own bottom margin already spaces them from what follows.
const Footnote = styled.p`
    margin: 18px 0 0;
    text-align: center;
    font-size: 14px;
    color: ${MUTE};
`;

const viewEnter = keyframes`
    from {
        opacity: 0;
        transform: translateY(12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const ViewSwap = styled.div`
    animation: ${viewEnter} ${VIEW_SWAP_MS}ms ease both;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

// Takes the place of the cards, so the seller can describe their case on its own.
const DescribeForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 640px;
    margin: 40px auto 0;
`;

const DescribeLabel = styled.label`
    text-align: center;
    font-size: 14px;
    color: ${MUTE};
`;

const DescribeArea = styled(TextArea)`
    min-height: 120px;
    padding: 12px 14px;
    font-size: 15px;
`;

const DescribeActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    font-size: 14px;
`;

type SolutionCardProps = {
    tool: OnboardingTool;
    isOpenedFromApp: boolean;
    onSelect: (tool: OnboardingTool) => void;
};

const SolutionCard = ({ tool, isOpenedFromApp, onSelect }: SolutionCardProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    // play() rejects when a pause interrupts it before it starts, which is expected on a quick hover.
    const play = () => videoRef.current?.play().catch(() => undefined);
    const pause = () => videoRef.current?.pause();

    return (
        <Card
            href={tool.url}
            onClick={(event) => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                onSelect(tool);
            }}
            onMouseEnter={play}
            onMouseLeave={pause}
            onFocus={play}
            onBlur={pause}
        >
            {tool.id === MOST_POPULAR_ID && <PopularBadge>{T._('Most popular', 'Admin')}</PopularBadge>}
            <Media $background={tool.tint}>
                <Video ref={videoRef} src={tool.video} loop muted playsInline preload='metadata' aria-hidden='true' />
                <Preview aria-hidden='true'>
                    <Icon>
                        <PlayArrowIcon />
                    </Icon>
                    {T._('Preview', 'Admin')}
                </Preview>
            </Media>
            <ToolName>
                <tool.logo aria-hidden='true' />
                <ToolNameText>{tool.name}</ToolNameText>
            </ToolName>
            <CardPart>
                <Goal>{tool.goal}</Goal>
            </CardPart>
            <CardPart>
                <Summary>{tool.summary}</Summary>
            </CardPart>
            <CardPart>
                <BuiltFor>
                    <BuiltForLabel>{T._('Built for', 'Admin')}</BuiltForLabel>
                    {tool.builtFor}
                </BuiltFor>
            </CardPart>
            <CardPart>
                <Go>
                    {isOpenedFromApp ? tool.goToLabel : tool.ctaLabel} <GoArrow aria-hidden='true'>→</GoArrow>
                </Go>
            </CardPart>
        </Card>
    );
};

type Props = {
    // Reopened from the solution switcher, by a seller who is already working inside a tool.
    isOpenedFromApp: boolean;
    // What the seller described here before, when they come back from the suggestion it led to: the box
    // opens again with it.
    initialPrompt?: string;
    // Opens on the description box rather than on the cards, as when the seller asked Zakeke from the app.
    isDescribeOpen: boolean;
    onViewChange: (describe: boolean) => void;
    isLoading: boolean;
    // Runs the suggestion on what the seller wrote in the description box.
    onDescribe: (text: string) => void;
    onSolutionSelect: (tool: OnboardingTool) => void;
};

const OnboardingSolutions = ({ isOpenedFromApp, initialPrompt, isDescribeOpen, onViewChange, isLoading, onDescribe, onSolutionSelect }: Props) => {
    const view = isDescribeOpen ? 'describe' : 'cards';
    const [prompt, setPrompt] = useState(initialPrompt ?? '');
    const promptRef = useRef<HTMLTextAreaElement>(null);

    // The box takes the focus as soon as it has come in.
    useEffect(() => {
        if (view === 'describe') promptRef.current?.focus();
    }, [view]);

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        onDescribe(prompt);
    };

    return (
        <Wrapper>
            <Intro>
                <Title>{T._('Find the right solution', 'Admin')}</Title>
                <Subtitle>{T._('Pick what you want to achieve.', 'Admin')}</Subtitle>
            </Intro>

            <ViewSwap key={view}>
                {view === 'cards' ? (
                    <>
                        <Grid>
                            {LISTED_TOOLS.map((tool) => (
                                <SolutionCard key={tool.id} tool={tool} isOpenedFromApp={isOpenedFromApp} onSelect={onSolutionSelect} />
                            ))}
                        </Grid>

                        <Footnote>
                            {T._('Not sure yet?', 'Admin')}{' '}
                            <TextLink type='button' onClick={() => onViewChange(true)}>
                                {T._('Describe what you need', 'Admin')}
                            </TextLink>{' '}
                            {T._("and we'll point you to one.", 'Admin')}
                        </Footnote>
                    </>
                ) : (
                    <DescribeForm onSubmit={onSubmit}>
                        <DescribeLabel htmlFor='onboarding-solutions-describe'>
                            {T._("Describe what you need and we'll point you to the right solution.", 'Admin')}
                        </DescribeLabel>
                        <DescribeArea
                            id='onboarding-solutions-describe'
                            ref={promptRef}
                            rows={4}
                            maxLength={2000}
                            disabled={isLoading}
                            value={prompt}
                            placeholder={PROMPT_PLACEHOLDER}
                            onChange={(event) => setPrompt(event.target.value)}
                        />
                        <DescribeActions>
                            {/* No Cancel here: the top bar's close (×) already leaves this flow. */}
                            <PrimaryButton type='submit' $size='large' disabled={!prompt.trim() || isLoading}>
                                {isLoading ? T._('Looking for the right solution...', 'Admin') : T._('Continue', 'Admin')}
                            </PrimaryButton>
                        </DescribeActions>
                    </DescribeForm>
                )}
            </ViewSwap>
        </Wrapper>
    );
};

export default OnboardingSolutions;
